import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { formatCurrency } from "../../utils/helpers";

/**
 * Spend by Category – pie chart with legend on the right (name, color, $amount (percentage%)).
 */
const CHART_COLORS = [
  "#2563eb", "#059669", "#ea580c", "#0d9488", "#7c3aed", "#dc2626",
  "#db2777", "#0284c7", "#ca8a04", "#65a30d", "#0891b2", "#4f46e5",
];
function getColorForIndex(index) {
  return CHART_COLORS[index % CHART_COLORS.length];
}

const CategorySpendingChart = ({ data = [] }) => {
  const raw = data || [];
  const chartData = raw.length === 1
    ? [
        { ...raw[0], category: raw[0].category, totalMRC: raw[0].totalMRC / 3, percentage: (parseFloat(raw[0].percentage || 0) / 3).toFixed(1) },
        { ...raw[0], category: raw[0].category, totalMRC: raw[0].totalMRC / 3, percentage: (parseFloat(raw[0].percentage || 0) / 3).toFixed(1) },
        { ...raw[0], category: raw[0].category, totalMRC: raw[0].totalMRC / 3, percentage: (parseFloat(raw[0].percentage || 0) / 3).toFixed(1) },
      ]
    : raw;

  const dataWithColor = chartData.map((item, index) => ({
    ...item,
    color: getColorForIndex(index),
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div className="bg-white px-3 py-2.5 border border-gray-200 rounded-lg shadow-lg text-sm">
          <p className="font-semibold text-gray-900">{d.category}</p>
          <p className="text-gray-600 mt-0.5 tabular-nums">
            {formatCurrency(d.totalMRC)} <span className="text-gray-500">({d.percentage}%)</span>
          </p>
        </div>
      );
    }
    return null;
  };

  if (!raw.length) {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
        <h3 className="text-base font-semibold text-gray-800 mb-2">Spend by Category</h3>
        <p className="text-sm text-gray-500 text-center py-6">No data available</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="px-4 pt-4 pb-1">
        <h3 className="text-base font-semibold text-gray-800">Spend by Category</h3>
      </div>
      <div className="flex flex-row items-stretch gap-0" style={{ minHeight: "380px" }}>
        <div className="flex-1 flex items-center justify-center min-w-0 py-2">
          <ResponsiveContainer width="100%" height={380}>
            <PieChart margin={{ top: 12, right: 12, bottom: 12, left: 12 }}>
              <Pie
                data={dataWithColor}
                cx="50%"
                cy="50%"
                outerRadius={95}
                paddingAngle={3}
                dataKey="totalMRC"
                nameKey="category"
                stroke="#fff"
                strokeWidth={2.5}
                isAnimationActive={true}
              >
                {dataWithColor.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getColorForIndex(index)} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "transparent" }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="w-px bg-gray-100 self-stretch" aria-hidden />
        <div className="flex flex-col justify-start gap-2 py-4 pl-4 pr-4 overflow-y-auto min-w-[200px] max-w-[220px] max-h-[380px]">
          {dataWithColor.map((entry, index) => (
            <div
              key={entry.category + index}
              className="flex items-center gap-2.5 flex-shrink-0 py-1 px-2 -mx-2 rounded-md hover:bg-gray-50 transition-colors"
            >
              <span
                className="flex-shrink-0 w-3.5 h-3.5 rounded-md border border-gray-200/80 shadow-sm"
                style={{ backgroundColor: getColorForIndex(index) }}
              />
              <span className="text-sm text-gray-800 truncate">
                <span className="font-medium text-gray-900">{entry.category}</span>
                <span className="text-gray-600 ml-1 tabular-nums">
                  {formatCurrency(entry.totalMRC)} <span className="text-gray-500">({entry.percentage}%)</span>
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CategorySpendingChart;
